import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { SERVER } from '../../../../config/config';
import { usePermission } from '../../../../hooks/usePermission';
import { 
  FaPlus, FaEdit, FaTrash, FaEye, FaToggleOn, FaToggleOff, 
  FaSearch, FaFilter, FaSave, FaTimes, FaImage, FaSort
} from 'react-icons/fa';

const Services = () => {
  const { can } = usePermission();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    short_desc: '',
    description: '',
    tags: [],
    image_position: 'right',
    icon: '',
    images: [], // Array for up to 3 new images
    existing_images: [], // Array of existing image URLs
    order_index: 0,
    active: true,
    reviews: []
  });

  const canEdit = can('content.edit');
  const canCreate = can('content.create');
  const canDelete = can('content.delete');

  useEffect(() => {
    fetchServices();
  }, [currentPage, searchTerm, filterActive]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...(filterActive !== 'all' && { active: filterActive === 'active' })
      };

      const response = await axios.get(`${SERVER}/services/admin/list`, {
        params,
        withCredentials: true
      });

      setServices(response.data.services || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      console.error('Error fetching services:', error);
      setServices([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate && !editingService) return;
    if (!canEdit && editingService) return;

    try {
      const formDataToSend = new FormData();
      
      // Append basic fields
      Object.keys(formData).forEach(key => {
        if (key === 'tags' || key === 'reviews') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else if (key === 'images') {
          // Append new image files (up to 3)
          if (Array.isArray(formData[key])) {
            formData[key].forEach(file => {
              if (file instanceof File) {
                formDataToSend.append('images', file);
              }
            });
          }
        } else if (key === 'existing_images') {
          // Send existing images to preserve them
          if (formData[key] && formData[key].length > 0 && (!formData.images || formData.images.length === 0)) {
            formData[key].forEach((img, idx) => {
              const fieldName = idx === 0 ? 'existing_image' : `existing_image_${idx + 1}`;
              formDataToSend.append(fieldName, img);
            });
          }
        } else if (key !== 'images' && key !== 'existing_images') {
          formDataToSend.append(key, formData[key]);
        }
      });

      let response;
      if (editingService) {
        response = await axios.put(`${SERVER}/services/admin/${editingService.id}`, formDataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await axios.post(`${SERVER}/services/admin`, formDataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      if (response.status === 200 || response.status === 201) {
        fetchServices();
        closeModal();
      }
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Error saving service. Please try again.');
    }
  };

  const handleEdit = async (service) => {
    if (!canEdit) return;
    
    try {
      const response = await axios.get(`${SERVER}/services/admin/${service.id}`, {
        withCredentials: true
      });
      
      const serviceData = response.data;
      console.log('Fetched service data for editing:', serviceData); // Debug log
      
      // Collect existing images
      const existingImages = [
        serviceData.image,
        serviceData.image_2,
        serviceData.image_3
      ].filter(Boolean); // Remove null/undefined values
      
      console.log('Existing images:', existingImages); // Debug log
      
      setFormData({
        title: serviceData.title || '',
        slug: serviceData.slug || '',
        short_desc: serviceData.short_desc || '',
        description: serviceData.description || '',
        tags: serviceData.tags || [],
        image_position: serviceData.image_position || 'right',
        icon: serviceData.icon || '',
        images: [], // New images to upload
        existing_images: existingImages, // Existing images from server
        order_index: serviceData.order_index || 0,
        active: serviceData.active,
        reviews: serviceData.reviews || []
      });
      setEditingService(service);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching service details:', error);
      alert('Error loading service details. Please try again.');
    }
  };

  const handleDelete = async (service) => {
    if (!canDelete) return;
    
    if (window.confirm(`Are you sure you want to delete "${service.title}"?`)) {
      try {
        await axios.delete(`${SERVER}/services/admin/${service.id}`, {
          withCredentials: true
        });
        fetchServices();
        // Show success message
      } catch (error) {
        console.error('Error deleting service:', error);
        // Show error message
      }
    }
  };

  const handleToggleActive = async (service) => {
    if (!canEdit) return;
    
    try {
      await axios.put(`${SERVER}/services/admin/${service.id}`, {
        ...service,
        active: !service.active
      }, {
        withCredentials: true
      });
      fetchServices();
    } catch (error) {
      console.error('Error toggling service status:', error);
    }
  };

  const openCreateModal = () => {
    if (!canCreate) return;
    
    setFormData({
      title: '',
      slug: '',
      short_desc: '',
      description: '',
      tags: [],
      image_position: 'right',
      icon: '',
      images: [],
      existing_images: [],
      order_index: 0,
      active: true,
      reviews: []
    });
    setEditingService(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingService(null);
    setFormData({
      title: '',
      slug: '',
      short_desc: '',
      description: '',
      tags: [],
      image_position: 'right',
      icon: '',
      images: [],
      existing_images: [],
      order_index: 0,
      active: true,
      reviews: []
    });
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({
      ...formData,
      title,
      slug: generateSlug(title)
    });
  };

  const addTag = () => {
    setFormData({
      ...formData,
      tags: [...formData.tags, '']
    });
  };

  const updateTag = (index, value) => {
    const updatedTags = [...formData.tags];
    updatedTags[index] = value;
    setFormData({
      ...formData,
      tags: updatedTags
    });
  };

  const removeTag = (index) => {
    const updatedTags = formData.tags.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      tags: updatedTags
    });
  };

  const addReview = () => {
    setFormData({
      ...formData,
      reviews: [...formData.reviews, { reviewer_name: '', review_text: '', rating: 5 }]
    });
  };

  const updateReview = (index, field, value) => {
    const updatedReviews = [...formData.reviews];
    updatedReviews[index][field] = value;
    setFormData({
      ...formData,
      reviews: updatedReviews
    });
  };

  const removeReview = (index) => {
    const updatedReviews = formData.reviews.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      reviews: updatedReviews
    });
  };

  // Image management helpers
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 3); // Limit to 3 images
    setFormData({
      ...formData,
      images: files
    });
  };

  const removeExistingImage = (index) => {
    const updatedExistingImages = formData.existing_images.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      existing_images: updatedExistingImages
    });
  };

  const removeNewImage = (index) => {
    const updatedImages = formData.images.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      images: updatedImages
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Services Management</h2>
          <p className="text-slate-600">Manage your service offerings</p>
        </div>
        {canCreate && (
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <FaPlus />
            <span>Add Service</span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <FaFilter className="text-slate-400" />
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Services</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Services Table */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-4 font-semibold text-slate-700">Service</th>
                <th className="text-left p-4 font-semibold text-slate-700">Status</th>
                <th className="text-left p-4 font-semibold text-slate-700">Reviews</th>
                <th className="text-left p-4 font-semibold text-slate-700">Order</th>
                <th className="text-right p-4 font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {services.map((service, index) => (
                <tr key={service.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {service.image && (
                        <img
                          src={`${SERVER}${service.image}`}
                          alt={service.title}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      )}
                      <div>
                        <h3 className="font-semibold text-slate-800">{service.title}</h3>
                        <p className="text-sm text-slate-500">{service.slug}</p>
                        {service.short_desc && (
                          <p className="text-sm text-slate-600 mt-1 line-clamp-2">{service.short_desc}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <button
                      onClick={() => handleToggleActive(service)}
                      disabled={!canEdit}
                      className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        service.active
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      } ${!canEdit ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                    >
                      {service.active ? <FaToggleOn /> : <FaToggleOff />}
                      {service.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-slate-600">
                      {service.reviews_count || 0} reviews
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-slate-600">{service.order_index}</span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(service)}
                        disabled={!canEdit}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(service)}
                        disabled={!canDelete}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-200">
            <div className="text-sm text-slate-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">
                  {editingService ? 'Edit Service' : 'Add New Service'}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Service Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={handleTitleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter service title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    URL Slug *
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="service-url-slug"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Short Description
                </label>
                <textarea
                  value={formData.short_desc}
                  onChange={(e) => setFormData({ ...formData, short_desc: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description for service cards"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Full Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Detailed service description"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Icon Class
                  </label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="fa-solid fa-code"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Image Position
                  </label>
                  <select
                    value={formData.image_position}
                    onChange={(e) => setFormData({ ...formData, image_position: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Order Index
                  </label>
                  <input
                    type="number"
                    value={formData.order_index}
                    onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Service Images (Up to 3)
                </label>
                
                {/* Show existing images */}
                {editingService && formData.existing_images.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm text-slate-600 mb-2">Current Images ({formData.existing_images.length}):</p>
                    <div className="grid grid-cols-3 gap-4">
                      {formData.existing_images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={`${SERVER}${img}`}
                            alt={`Service image ${idx + 1}`}
                            className="w-full h-32 object-cover rounded-lg border-2 border-slate-300"
                            onError={(e) => {
                              console.error('Failed to load service image:', `${SERVER}${img}`);
                              e.target.style.opacity = '0.3';
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => removeExistingImage(idx)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-all duration-200 shadow-lg"
                            style={{ 
                              opacity: 1,
                              transform: 'scale(1)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            title="Remove image"
                          >
                            ×
                          </button>
                          <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded shadow">
                            Current {idx + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Show preview of new images */}
                {formData.images && formData.images.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm text-slate-600 mb-2">New Images to Upload ({formData.images.length}):</p>
                    <div className="grid grid-cols-3 gap-4">
                      {formData.images.map((file, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`New image ${idx + 1}`}
                            className="w-full h-32 object-cover rounded-lg border-2 border-blue-300"
                          />
                          <button
                            type="button"
                            onClick={() => removeNewImage(idx)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-all duration-200 shadow-lg"
                            style={{ 
                              opacity: 1,
                              transform: 'scale(1)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            title="Remove image"
                          >
                            ×
                          </button>
                          <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded shadow">
                            New {idx + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-sm text-slate-500 mt-2">
                  {editingService 
                    ? 'Select up to 3 images. Leave empty to keep current images. Selecting new images will replace all existing images.'
                    : 'Select up to 3 images for this service. First image will be the primary image.'}
                </p>
              </div>

              {/* Tags Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-semibold text-slate-700">
                    Service Tags
                  </label>
                  <button
                    type="button"
                    onClick={addTag}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    <FaPlus />
                    Add Tag
                  </button>
                </div>
                
                <div className="space-y-2">
                  {formData.tags.map((tag, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={tag}
                        onChange={(e) => updateTag(index, e.target.value)}
                        placeholder="Tag name"
                        className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => removeTag(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Reviews Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-semibold text-slate-700">
                    Customer Reviews
                  </label>
                  <button
                    type="button"
                    onClick={addReview}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    <FaPlus />
                    Add Review
                  </button>
                </div>
                
                <div className="space-y-4">
                  {formData.reviews.map((review, index) => (
                    <div key={index} className="flex gap-4 items-start p-4 bg-slate-50 rounded-xl">
                      <div className="flex-1 space-y-3">
                        <input
                          type="text"
                          value={review.reviewer_name}
                          onChange={(e) => updateReview(index, 'reviewer_name', e.target.value)}
                          placeholder="Reviewer name"
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <textarea
                          value={review.review_text}
                          onChange={(e) => updateReview(index, 'review_text', e.target.value)}
                          placeholder="Review text"
                          rows={2}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <select
                          value={review.rating}
                          onChange={(e) => updateReview(index, 'rating', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value={5}>5 Stars</option>
                          <option value={4}>4 Stars</option>
                          <option value={3}>3 Stars</option>
                          <option value={2}>2 Stars</option>
                          <option value={1}>1 Star</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeReview(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-4 pt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-3 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <FaSave />
                  <span>{editingService ? 'Update Service' : 'Create Service'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;